import { useRef, useState } from "react";

import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from "recharts";

import { DataExportButton } from "src/components/dataView/DataExport/DataExportButton";
import Logo from "src/components/icon/Logo";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "src/components/ui/chart";
import { storeIntegrationTypes } from "src/lib/constants";
import { dateFormat } from "src/lib/format";
import { useIntegration } from "src/lib/hooks/useIntegration";
import { useNumberFormat } from "src/lib/hooks/useNumberFormat";
import { cn } from "src/lib/utils";

const chartConfig = {
  top: {
    label: "Value",
  },
};

const ResponsiveAxisTick = ({ x, y, visibleTicksCount, width, payload }) => {
  const tickWidth = width / visibleTicksCount - 1;
  return (
    <g transform={`translate(${x},${y})`}>
      <switch>
        <foreignObject
          x={-tickWidth / 2}
          y={0}
          dy={16}
          height={40}
          width={tickWidth}
        >
          <p
            xmlns="http://www.w3.org/1999/xhtml"
            className="text-muted-foreground overflow-hidden text-center overflow-ellipsis"
          >
            {payload.value}
          </p>
        </foreignObject>

        <text x={0} y={0} dy={16}>
          {payload.value}
        </text>
      </switch>
    </g>
  );
};

const VerticalAxisTick = ({ y, payload }) => (
  <text y={y} dy={4} textAnchor="start" className="text-muted-foreground">
    {payload.value}
  </text>
);

const WaterfallChart = ({
  data,
  hasData,
  loading,
  dateFrom,
  dateTo,
  className,
  containerClassName,
  title,
  footer,
  orientation = "responsive", // 'responsive', 'vertical' or 'horizontal'
  imageExportRef: _imageExportRef,
}) => {
  const { selectedIntegration } = useIntegration(
    storeIntegrationTypes.shopify,
    {
      queryOnRender: false,
    }
  );

  const numberFormat = useNumberFormat(selectedIntegration?.currency);

  const ref = useRef(null);
  const imageExportRef = _imageExportRef || ref;
  const [isResponsiveVertical, setIsResponsiveVertical] = useState(false);
  const vertical =
    orientation === "vertical"
      ? true
      : orientation === "horizontal"
        ? false
        : isResponsiveVertical;

  const MainAxis = vertical ? YAxis : XAxis;
  const valueLabelWidth = Math.max(
    0,
    ...data.map((item) => numberFormat.currency(item.top).length * 7)
  );

  return (
    <Card
      className={cn("relative h-fit w-full", className)}
      ref={imageExportRef}
    >
      <CardHeader>
        <CardTitle>
          {title}

          {selectedIntegration && (
            <span data-export-show className="hidden">
              {` (${selectedIntegration.description})`}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          {dateFrom && dateTo && hasData
            ? `${dateFormat.default(dateFrom)} - ${dateFormat.default(dateTo)}`
            : "Loading..."}
        </CardDescription>

        {/* Export Button */}
        <DataExportButton
          disabled={loading}
          imageExportRef={imageExportRef}
          title={title}
          integrationName={selectedIntegration?.description}
          dateFrom={dateFrom}
          dateTo={dateTo}
          className="absolute top-2 right-2"
          asIcon
        />
      </CardHeader>
      <CardContent>
        <ChartContainer
          className={cn(
            "h-fit min-h-1 min-w-1",
            vertical && "sm:aspect-video",
            containerClassName
          )}
          config={chartConfig}
          onResize={(width) => {
            setIsResponsiveVertical(width < 500);
          }}
        >
          <BarChart
            accessibilityLayer
            data={data}
            overflow="visible"
            layout={vertical ? "vertical" : "horizontal"}
            margin={{
              right: vertical ? valueLabelWidth : 0,
            }}
            barSize={vertical ? 12 : undefined}
          >
            <MainAxis
              dataKey="name"
              tickLine={false}
              axisLine={false}
              tick={vertical ? <VerticalAxisTick /> : <ResponsiveAxisTick />}
              interval={0}
              width={150}
              type="category"
            />

            {vertical && (
              <XAxis
                type="number"
                dataKey="bottom"
                domain={["dataMin", "dataMax"]}
                hide
              />
            )}
            <Bar
              dataKey="bottom"
              stackId="a"
              layout={vertical ? "vertical" : "horizontal"}
              fill="transparent"
            />
            <Bar
              dataKey="top"
              stackId="a"
              radius={4}
              layout={vertical ? "vertical" : "horizontal"}
            >
              {hasData && (
                <LabelList
                  position={vertical ? "right" : "top"}
                  dataKey="top"
                  fillOpacity={1}
                  fontSize={12}
                  formatter={numberFormat.currency}
                  offset={5}
                />
              )}
              {data.map((item) => (
                <Cell
                  key={item.name}
                  fill={
                    item.top >= 0 ? "var(--chart-good)" : "var(--chart-bad)"
                  }
                  style={{
                    filter: loading ? "blur(1px)" : "none",
                  }}
                />
              ))}
            </Bar>
            {hasData && (
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    isItemHidden={(item) => item.name === "bottom"}
                    isItemLabelHidden={() => true}
                    valueFormatter={(value) => numberFormat.currency(value)}
                    getItemColor={({ payload }) =>
                      payload.top >= 0
                        ? "var(--chart-good)"
                        : "var(--chart-bad)"
                    }
                  />
                }
                cursor={false}
              />
            )}
          </BarChart>
        </ChartContainer>

        {/* Watermark */}
        <div
          data-export-show
          className="text-muted-foreground absolute right-2 bottom-2 flex hidden items-center gap-1 text-xs"
        >
          <Logo inline className="h-4" />
        </div>
      </CardContent>
      {footer && (
        <CardFooter className="flex-col items-start gap-2 text-sm">
          {footer}
        </CardFooter>
      )}
    </Card>
  );
};

export default WaterfallChart;
